import { lazy } from 'react';
import type { RouteObject } from 'react-router';

import RootLayout from '@/components/layout/RootLayout';
import HomePage from '@/pages/HomePage';
import { ROUTE_SEGMENTS, SUPPORTED_LOCALES, localePrefix, type Locale } from '@/i18n/locales';

// Route-level splitting: a visitor landing on the home page never downloads the
// fleet or booking bundles.
const TripPage = lazy(() => import('@/pages/TripPage'));
const BookingConfirmationPage = lazy(() => import('@/pages/BookingConfirmationPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const FleetPage = lazy(() => import('@/pages/FleetPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const ReviewPage = lazy(() => import('@/pages/ReviewPage'));
const ReviewsListPage = lazy(() => import('@/pages/ReviewsListPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

function childRoutes(locale: Locale): RouteObject[] {
  const seg = (key: keyof typeof ROUTE_SEGMENTS): string => ROUTE_SEGMENTS[key][locale];

  return [
    { index: true, element: <HomePage /> },
    { path: `${seg('trips')}/:slug`, element: <TripPage /> },
    { path: `${seg('booking')}/:code`, element: <BookingConfirmationPage /> },
    { path: seg('fleet'), element: <FleetPage /> },
    { path: seg('about'), element: <AboutPage /> },
    { path: seg('contact'), element: <ContactPage /> },
    { path: `${seg('review')}/:token`, element: <ReviewPage /> },
    { path: seg('reviews'), element: <ReviewsListPage /> },
    { path: '*', element: <NotFoundPage /> },
  ];
}

/**
 * One route tree per locale, each rooted at its own prefix. The default locale
 * lives at `/`, the others under `/<locale>`.
 */
export const routes: RouteObject[] = [
  // The back office sits outside the localized trees and outside the public
  // layout: it is staff-facing, never indexed, and its bundle is code-split so a
  // public visitor never downloads it.
  { path: '/admin', element: <AdminPage /> },

  ...SUPPORTED_LOCALES.map((locale) => ({
    path: `${localePrefix(locale)}/`,
    element: <RootLayout locale={locale} />,
    children: childRoutes(locale),
  })),
];

/** Routes emitted as static HTML at build time. */
export const PRERENDER_ROUTES: string[] = SUPPORTED_LOCALES.flatMap((locale) => {
  const prefix = localePrefix(locale);
  return [
    prefix === '' ? '/' : prefix,
    `${prefix}/${ROUTE_SEGMENTS.fleet[locale]}`,
    `${prefix}/${ROUTE_SEGMENTS.about[locale]}`,
    `${prefix}/${ROUTE_SEGMENTS.contact[locale]}`,
  ];
});

export default routes;
